import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Plus, Power, QrCode, RefreshCw, Trash2 } from 'lucide-react'
import { waApi, type WaSession } from '../../api/whatsapp'
import { usePageTitle } from '../../hooks/usePageTitle'

const STATUS_BADGE: Record<WaSession['status'], string> = {
  connected: 'bg-green-100 text-green-700',
  qr_pending: 'bg-amber-100 text-amber-700',
  created: 'bg-gray-100 text-gray-600',
  disconnected: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
}

export function WhatsappSettings() {
  usePageTitle('WhatsApp')
  const { companyId } = useParams()
  const qc = useQueryClient()
  const [qrSession, setQrSession] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WaSession | null>(null)
  const [newName, setNewName] = useState('')
  const emptyKc = { firstName: '', lastName: '', description: '', phoneNumber: '', email: '' }
  const [kc, setKc] = useState(emptyKc)

  const { data: sessions = [] } = useQuery({
    queryKey: ['wa-sessions', companyId],
    queryFn: () => waApi.listSessions(companyId!),
    refetchInterval: 10_000,
  })
  const { data: known = [] } = useQuery({
    queryKey: ['wa-known', companyId],
    queryFn: () => waApi.listKnown(companyId!),
  })

  const inv = (key: string) => () => qc.invalidateQueries({ queryKey: [key, companyId] })
  const createSession = useMutation({
    mutationFn: () => waApi.createSession(companyId!, newName),
    onSuccess: (s: any) => {
      setNewName('')
      inv('wa-sessions')()
      setQrSession(s.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  })
  const addKnown = useMutation({
    mutationFn: () => waApi.createKnown(companyId!, {
      firstName: kc.firstName.trim(),
      lastName: kc.lastName.trim(),
      description: kc.description.trim() || undefined,
      phoneNumber: kc.phoneNumber.trim(),
      email: kc.email.trim() || undefined,
    }),
    onSuccess: () => {
      setKc(emptyKc)
      inv('wa-known')()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  })

  return (
    <div className="max-w-3xl space-y-8">
      <section>
        <h2 className="mb-1 text-lg font-semibold">WhatsApp numbers</h2>
        <p className="mb-3 text-sm text-amber-700">
          ⚠ This integration uses an unofficial WhatsApp gateway. WhatsApp's terms do not allow third-party
          clients and numbers can be blocked. Use a dedicated number where possible.
        </p>
        <div className="mb-4 flex gap-2">
          <input
            className="input flex-1"
            placeholder="Display name, e.g. Sales line 1"
            value={newName}
            onChange={e => setNewName(e.target.value)}
          />
          <button
            className="btn btn-primary"
            disabled={!newName.trim() || createSession.isPending}
            onClick={() => createSession.mutate()}
          >
            <Plus size={16} /> Add number
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1">Name</th>
              <th>Number</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map(s => (
              <tr key={s.id} className="border-t">
                <td className="py-2 font-medium">{s.name}</td>
                <td>{s.phoneNumber ?? '—'}</td>
                <td>
                  <span className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[s.status]}`}>{s.status}</span>
                </td>
                <td className="space-x-1 text-right">
                  {s.status !== 'connected' && (
                    <button className="btn btn-ghost btn-sm" title="Show QR" onClick={() => setQrSession(s.id)}>
                      <QrCode size={15} />
                    </button>
                  )}
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Restart"
                    onClick={() => waApi.restartSession(companyId!, s.id).then(inv('wa-sessions'))}
                  >
                    <RefreshCw size={15} />
                  </button>
                  {s.status === 'connected' && (
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Logout (keeps chat history)"
                      onClick={() =>
                        waApi.logoutSession(companyId!, s.id).then(() => {
                          inv('wa-sessions')()
                          toast.success('Logged out - chat history kept. Scan the QR to reconnect.')
                        })
                      }
                    >
                      <Power size={15} />
                    </button>
                  )}
                  <button
                    className="btn btn-ghost btn-sm text-red-600"
                    title="Delete (removes all chat history!)"
                    onClick={() => setDeleteTarget(s)}
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sessions.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            <Power size={12} className="mb-0.5 inline" /> <strong>Logout</strong> disconnects the number but keeps all
            chat history - use it to reconnect or switch phones.
            <strong className="text-red-600"> Delete</strong> permanently removes the number and every chat. This can't be undone.
          </p>
        )}
      </section>

      <section>
        <h2 className="mb-1 text-lg font-semibold">Known contacts</h2>
        <p className="mb-3 text-sm text-gray-500">
          Saved people your team can add to WhatsApp groups. Only contacts with a WhatsApp number can be added to a group.
        </p>
        <div className="mb-3 grid grid-cols-2 gap-2">
          <input className="input" placeholder="First name" value={kc.firstName}
            onChange={e => setKc(v => ({ ...v, firstName: e.target.value }))} />
          <input className="input" placeholder="Last name" value={kc.lastName}
            onChange={e => setKc(v => ({ ...v, lastName: e.target.value }))} />
          <input className="input" placeholder="WhatsApp number e.g. 49151… (required)" value={kc.phoneNumber}
            onChange={e => setKc(v => ({ ...v, phoneNumber: e.target.value }))} />
          <input className="input" placeholder="Email (optional)" value={kc.email}
            onChange={e => setKc(v => ({ ...v, email: e.target.value }))} />
          <input className="input col-span-2" placeholder="Description (optional)" value={kc.description}
            onChange={e => setKc(v => ({ ...v, description: e.target.value }))} />
        </div>
        <div className="mb-3 flex justify-end">
          <button
            className="btn btn-primary"
            disabled={!kc.firstName.trim() || !kc.lastName.trim() || !kc.phoneNumber.trim() || addKnown.isPending}
            onClick={() => addKnown.mutate()}
          >
            <Plus size={16} /> Add contact
          </button>
        </div>
        {known.map(k => (
          <div key={k.id} className="flex items-start justify-between border-t py-1.5 text-sm">
            <div>
              <span className="font-medium">{k.firstName} {k.lastName}</span>
              {k.phoneNumber && <span className="ml-2 text-gray-500">{k.phoneNumber}</span>}
              {!k.phoneNumber && <span className="ml-2 text-xs text-amber-600">no WhatsApp number</span>}
              {k.email && <span className="ml-2 text-gray-400">{k.email}</span>}
              {k.description && <div className="text-xs text-gray-400">{k.description}</div>}
            </div>
            <button
              className="btn btn-ghost btn-sm text-red-600"
              onClick={() => waApi.deleteKnown(companyId!, k.id).then(inv('wa-known'))}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </section>

      {/* <p className="text-xs text-gray-400">
        Powered by Evolution API (self-hosted) — this notice is required by the Evolution API license.
      </p> */}

      {createSession.isPending && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/40">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-green-500" />
          <p className="mt-3 text-sm text-white">Creating WhatsApp session…</p>
        </div>
      )}

      {qrSession && (
        <QrModal companyId={companyId!} sessionId={qrSession} onClose={() => { setQrSession(null); inv('wa-sessions')() }} />
      )}

      {deleteTarget && (
        <DeleteSessionModal
          companyId={companyId!}
          session={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); inv('wa-sessions')() }}
        />
      )}
    </div>
  )
}

function DeleteSessionModal({
  companyId,
  session,
  onClose,
  onDone,
}: {
  companyId: string
  session: WaSession
  onClose: () => void
  onDone: () => void
}) {
  const del = useMutation({
    mutationFn: () => waApi.deleteSession(companyId, session.id),
    onSuccess: () => { toast.success('Number and chat history deleted'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })
  const logout = useMutation({
    mutationFn: () => waApi.logoutSession(companyId, session.id),
    onSuccess: () => { toast.success('Logged out — chat history kept. Scan the QR to reconnect.'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to logout'),
  })
  const busy = del.isPending || logout.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-[28rem] rounded-lg bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="mb-2 font-semibold text-red-600">Delete "{session.name}"?</h3>
        <p className="text-sm text-gray-600">
          This permanently removes the number <strong>and all of its chat history</strong> (every chat, message and note)
          from the CRM. This <strong>cannot be undone</strong>.
        </p>
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Just want to reconnect or switch phones? Use <strong>Log out</strong> instead — it disconnects the number but
          keeps all chat history, and you can scan the QR again to reconnect.
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          {session.status === 'connected' && (
            <button className="btn" onClick={() => logout.mutate()} disabled={busy}>
              Log out (keep history)
            </button>
          )}
          <button className="btn btn-danger" onClick={() => del.mutate()} disabled={busy}>
            Delete everything
          </button>
        </div>
      </div>
    </div>
  )
}

function QrModal({
  companyId,
  sessionId,
  onClose,
}: {
  companyId: string
  sessionId: string
  onClose: () => void
}) {
  // Poll status+QR every 2s (faster than the 10s sessions list). `status` comes
  // straight from our DB and flips to 'connected' as soon as the webhook lands.
  const { data } = useQuery({
    queryKey: ['wa-qr', companyId, sessionId],
    queryFn: () => waApi.sessionQr(companyId, sessionId),
    refetchInterval: 2_000,
  })
  const connected = data?.status === 'connected'
  useEffect(() => {
    if (connected) {
      const t = setTimeout(onClose, 1500) // let the user see the success tick
      return () => clearTimeout(t)
    }
  }, [connected, onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="rounded-lg bg-white p-6 text-center shadow-xl" onClick={e => e.stopPropagation()}>
        {connected ? (
          <div className="flex h-72 w-64 flex-col items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl text-green-600">✓</div>
            <p className="mt-4 font-semibold text-green-700">Connected</p>
            <p className="mt-1 text-sm text-gray-500">This number is now linked.</p>
          </div>
        ) : (
          <>
            <h3 className="mb-2 font-semibold">Scan with WhatsApp</h3>
            <p className="mb-3 max-w-xs text-sm text-gray-500">
              On the phone: WhatsApp → Settings → Linked devices → Link a device
            </p>
            {data?.qr ? (
              <img src={data.qr} className="mx-auto h-64 w-64" alt="WhatsApp QR code" />
            ) : (
              <div className="flex h-64 w-64 flex-col items-center justify-center gap-3 text-gray-400">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-green-500" />
                <span className="text-sm">Preparing QR code…</span>
              </div>
            )}
            <button className="btn btn-ghost mt-3" onClick={onClose}>Close</button>
          </>
        )}
      </div>
    </div>
  )
}
